import { indexRemindersById } from "../utils/reminderMapHelpers";
import { indexEventsById } from "../utils/eventMapHelpers";
import {
  createContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { weekDays } from "../constants/dateAndTimeConstants";
import { holidays } from "../constants/holidays";
import {
  getUserData,
  getGoogleData,
  requestAndSubscribe,
  getNotifications,
  getNotificationsAtStart,
  addSubscriptionToUser,
  getGoogleCalendarEvents,
  markAsRead,
  getFriendInfo,
  getUserDataFresh,
  API_GetLocation,
  loginWithGoogle,
  checkPermissionsAndCreateNewSub,
  updateLastSeenOnDevice,
} from "../utils/api";
import QRCode from "qrcode-generator";
import IndexedDBManager from "../utils/indexDBApi";
import {
  getAuthToken,
  getTimers,
  H_FetchWeather,
  parseNotifSubs,
} from "../utils/helpers";

const UserContext = createContext({});

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(false);
  const [authToken, setAuthToken] = useState(getAuthToken);
  // const [googleEvents, setGoogleEvents] = useState([]);
  const [preferences, setPreferences] = useState(
    JSON.parse(localStorage.getItem("preferences")) || {},
  );
  const [upcoming, setUpcoming] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [lists, setLists] = useState([]);
  const [remindersMap, setRemindersMap] = useState(new Map());
  const remindersById = useMemo(
    () => indexRemindersById(remindersMap),
    [remindersMap],
  );
  const getReminderById = useCallback(
    (id) => (id == null ? undefined : remindersById.get(String(id))),
    [remindersById],
  );
  const [stickies, setStickies] = useState([]);
  const [userTasks, setUserTasks] = useState([]);
  const [friends, setFriends] = useState([]);
  const [kanbans, setKanbans] = useState([]);
  const [connectionRequests, setConnectionRequests] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [localDB, setLocalDB] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [systemNotif, setSystemNotif] = useState({ show: false });
  const [backOnlineTrigger, setBackOnlineTrigger] = useState(false);
  const [googleToken, setGoogleToken] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [eventMap, setEventMap] = useState(new Map());
  const [eventMapDays, setEventMapDays] = useState(new Map());
  const eventsById = useMemo(() => indexEventsById(eventMap), [eventMap]);
  const getEventById = useCallback(
    (id) => (id == null ? undefined : eventsById.get(String(id))),
    [eventsById],
  );
  const [timers, setTimers] = useState(getTimers() || []);
  // const [timers, setTimers] =
  //   useState(
  // Uncomment for default timer and debugging
  // [
  // {
  //   startTime: new Date().toString(),
  //   endTime: new Date(
  //     new Date().setMinutes(new Date().getMinutes() + 10),
  //   ).toString(),
  //   howLongMS: 600000,
  //   pauseCount: 0,
  //   pinned: true,
  //   id: "1",
  // },
  // ]
  // )

  const [location, setLocation] = useState({
    city: "",
    state: "",
    lng: "",
    lat: "",
  });
  const [usersLocations, setUsersLocations] = useState([]); // {city: "", state: "", lng: "", lat: ""}
  const [weatherData, setWeatherData] = useState(null);
  const [notifSubs, setNotifSubs] = useState([]);
  const [reminderNotifications, setReminderNotifications] = useState([
    // Test. Uncomment to text out layout
    // {
    //   type: "reminder",
    //   time: new Date(),
    //   read: false,
    //   readTime: "",
    //   notifData: {
    //     eventRefIId: null,
    //     time: new Date(),
    //     notes: "This is my test reminder to test UI",
    //     title: "This Is My Test Title",
    //     userId: 0,
    //   },
    //   userId: 0,
    //   sentNotification: false,
    //   sentWebPush: false,
    //   deviceExceptions: [],
    // },
  ]);

  // Refs
  const subscribedFresh = useRef(false);
  const sseConnectionRef = useRef(null);
  const sseUserIdRef = useRef(null);
  const sseRequestGenerationRef = useRef(0);

  // Update when necessary for indexedDB changes
  const myLocalDBVersion = Math.max(
    5,
    Number(import.meta.env.VITE_INDEXED_DB_VERSION) || 0,
  );

  const updateStatus = () => {
    setIsOnline(navigator.onLine);
  };

  useEffect(() => {
    const handleSWMessage = async (event) => {
      if (event.data && event.data.type === "user-cache-update") {
        try {
          const freshData = await getUserDataFresh();

          const responseTypeForUserData = freshData.headers["x-sw-source"];

          // The service worker will be first grabbing stale cache quickly. Make sure this is not the case before
          // calling updateUI(). User data MUST be server fresh because down the road notification subscriptions are made
          const isFresh = responseTypeForUserData === "network";

          if (freshData.status !== 200) {
            throw new Error(
              `No cache in service worker. Response status: ${res.status}`,
            );
          }

          const freshUser = freshData.data.user;
          updateUI(freshUser, isFresh);
        } catch (err) {
          console.log(err);
          console.log("No cache in service worker");
        }
      }
    };
    navigator.serviceWorker.addEventListener("message", handleSWMessage);
    return () =>
      navigator.serviceWorker.removeEventListener("message", handleSWMessage);
  }, []);

  useEffect(() => {
    if (localDB) {
      if (authToken) {
        localDB.setIndexedDBAuthToken(authToken);
      }

      M_GrabLocations();
    }
  }, [localDB, authToken]);

  useEffect(() => {
    const currentDate = new Date();
    const filteredEvents = [...eventsById.values()]
      .filter((event) => {
        const eventDate = new Date(event.startDate);
        const diff = Math.ceil(
          (eventDate - currentDate) / (25 * 60 * 60 * 1000),
        );
        return diff >= 0 && diff <= 8;
      })
      .map((event) => ({
        ...event,
        diff: Math.ceil(
          (new Date(event.startDate) - currentDate) / (25 * 60 * 60 * 1000),
        ),
      }))
      .sort((a, b) => a.diff - b.diff);
    setUpcoming(filteredEvents);
  }, [eventsById]);

  const M_GrabLocations = async () => {
    const dbLocations = (await localDB.getUserLocations()) || [];
    setUsersLocations(dbLocations);
  };

  const M_FetchLocation = async (lng, lat) => {
    try {
      const response = await API_GetLocation(lng, lat, 1);

      const data = response?.data;

      if (!data) {
        return;
      }

      const city = data[0]?.name;
      const state = data[0]?.state;

      if (!city || !state) {
        return;
      }

      const locationObj = {
        city: city,
        state: state,
        lng: lng,
        lat: lat,
      };

      setLocation(locationObj);

      const includes = usersLocations.some(
        (l) => l.city === city && l.state === state,
      );

      if (!includes) {
        setUsersLocations((prev) => [...prev, locationObj]);
      }

      M_FetchWeather(lng, lat);
    } catch (err) {
      console.log(err);
    }
  };

  const M_FetchWeather = async (lng, lat) => {
    const weather = await H_FetchWeather(lng, lat);
    setWeatherData(weather);
  };

  useEffect(() => {
    // Immediately open indexDb
    createIndexedDb();
    navigator?.geolocation.getCurrentPosition((position) => {
      const long = position?.coords?.longitude;
      const lat = position?.coords?.latitude;

      if (long && lat) {
        M_FetchLocation(long, lat);
      }
    });
    window.addEventListener("online", updateStatus);
    window.addEventListener("offline", updateStatus);
    return () => {
      window.removeEventListener("online", updateStatus);
      window.removeEventListener("offline", updateStatus);
    };
  }, []);

  useEffect(() => {
    let refreshTimeout;
    if (!isOnline) {
      const newNotif = {
        show: true,
        title: "Network",
        text: "You are offline",
        color: "bg-red-300",
        hasCancel: true,
        actions: [
          {
            text: "close",
            func: () => setSystemNotif({ show: false }),
          },
          { text: "refresh", func: () => window.location.reload() },
        ],
      };
      refreshTimeout = setTimeout(() => {
        setRefresh(true);
      }, 600000);
      setSystemNotif(newNotif);
      setBackOnlineTrigger(true);
    }
    if (isOnline && backOnlineTrigger === true) {
      if (!refresh) {
        clearTimeout(refreshTimeout);
      }
      // Logic to show notification when going back online
      const newNotif = {
        show: true,
        title: "Network",
        text: "You are back online",
        color: "bg-green-300",
        hasCancel: true,
        actions: [
          {
            text: "close",
            func: () => setSystemNotif({ show: false }),
          },
        ],
      };
      setSystemNotif(newNotif);
      setBackOnlineTrigger(false);
      if (refresh) {
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    }
  }, [isOnline]);

  const buildEventsMap = (allEvents) => {
    const newMap = new Map();

    allEvents.forEach((evt) => {
      const date = new Date(evt.date);
      const key = `${date.getFullYear()}-${date.getMonth()}`;

      if (!newMap.has(key)) {
        newMap.set(key, { events: [evt] });
      } else {
        newMap.get(key).events.push(evt);
      }

      if (evt.repeats.repeat) {
        if (!newMap.has("repeat-events")) {
          newMap.set("repeat-events", { events: [evt] });
        } else {
          newMap.get("repeat-events").events.push(evt);
        }
      }
    });
    setEventMap(new Map(newMap));
  };

  const buildRemindersMap = (allReminders) => {
    const newMap = new Map();

    allReminders.forEach((rmd) => {
      const date = new Date(rmd.time);
      const key = `${date.getFullYear()}-${date.getMonth()}`;

      if (!newMap.has(key)) {
        newMap.set(key, { reminders: [rmd] });
      } else {
        newMap.get(key).reminders.push(rmd);
      }

      if (rmd.repeats?.on) {
        if (!newMap.has("repeat-reminders")) {
          newMap.set("repeat-reminders", { reminders: [rmd] });
        } else {
          newMap.get("repeat-reminders").reminders.push(rmd);
        }
      }
    });
    setRemindersMap(new Map(newMap));
  };

  useEffect(() => {
    if (googleToken && !authToken) {
      completeAutomaticGoogleLogin();
    }
  }, [isOnline, googleToken]);

  const completeAutomaticGoogleLogin = async () => {
    const resetOnFailure = () => {
      setUser(false);
      setEventMap(new Map());
    };

    try {
      const googleLoginData = await getGoogleData(googleToken);

      try {
        if (googleLoginData?.data) {
          const CNGLoginResponse = await loginWithGoogle(googleLoginData.data);

          if (CNGLoginResponse?.data) {
            setUser(CNGLoginResponse.data.user);
            setAuthToken(CNGLoginResponse.data.token);
            localStorage.setItem("authToken", CNGLoginResponse.data.token);
          } else {
            throw new Error("loginWithGoogle() succeeded but returned no data");
          }
        } else {
          throw new Error("getGoogleData() succeeded but returned no data");
        }
      } catch (err) {
        console.log("Error logging in to CNG using Google");
        console.log(err);
        resetOnFailure();
      }
    } catch (err) {
      console.log("Error with grabbing Google login auth data");
      console.log(err);
      resetOnFailure();
    }
  };

  const M_HandleNotificationSubscriptions = async (freshUser) => {
    if (subscribedFresh.current === true) {
      return;
    }

    subscribedFresh.current = true;

    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      console.log("No serviceWorker in navigator and no pushManager in window");
      return;
    }

    const registration = await navigator.serviceWorker.ready;
    let currentSub = await registration.pushManager.getSubscription();

    // The user clearly has no notification subscription and potentially never accepted permissions
    if (!freshUser.notifSub || freshUser.notifSub.length < 1) {
      console.log(
        "User has no user.notifSub array, or user.notifSub.length is less then 1. Requesting new and returning",
      );
      // Remove any lingering subscriptions on the client

      if (currentSub) {
        currentSub.unsubscribe();
      }

      await M_RequestPermissionsAndSubscribe(authToken);
      return;
    }

    if (Notification.permission === "denied") {
      console.log("User denied Notifications");
      // Do something!
      return;
    }

    if (!currentSub) {
      console.log("No current sub creating new");
      currentSub = await checkPermissionsAndCreateNewSub();
      console.log("New currentSub created");
      console.log(currentSub);
      if (!currentSub) return;
    }

    const currentEndpoint = currentSub.endpoint;

    const storedSubs = Array.isArray(freshUser.notifSub)
      ? freshUser.notifSub
      : [];

    const userHasSub = storedSubs.some((storedSub) => {
      try {
        const parsed = JSON.parse(storedSub);
        return parsed?.endpoint === currentEndpoint;
      } catch (err) {
        console.log("Error parsing stored user subscriptions");
        return false;
      }
    });

    if (userHasSub) {
      console.log("User does have sub, running send");
      send(authToken, freshUser);
      return;
    }

    try {
      console.log(
        "User does not have subscription stored, creating new and server request for new user",
      );
      const newUserRes = await addSubscriptionToUser(currentSub, authToken);

      localStorage.setItem("authToken", newUserRes.data.token);
      localStorage.setItem("user", JSON.stringify(newUserRes.data.user));

      setUser(newUserRes.data.user);
      setNotifSubs(newUserRes.data.user.notifSub);
      send(newUserRes.data.token, newUserRes.data.user);
    } catch (err) {
      console.log("Error adding new subscription to user");
      console.log(err);
    }
  };

  const continueRequests = async (freshUser) => {
    if (freshUser.importedGoogleEvents) {
      const newNotif = {
        show: true,
        title: "Google Events",
        text: "Would you like to import your google calendar events?",
        color: "bg-sky-300",
        hasCancel: true,
        actions: [
          {
            text: "close",
            func: () => setSystemNotif({ show: false }),
          },
          {
            text: "get events",
            func: () => M_FetchGoogleEvents(googleToken),
          },
        ],
      };
      setSystemNotif(newNotif);
    }

    await M_HandleNotificationSubscriptions(freshUser);

    try {
      const friendInformation = await getFriendInfo(authToken);

      if (!friendInformation?.data) {
        throw new Error("getFriendInfo() succeeded but returned no data");
      }

      const data = friendInformation.data;
      setFriends(data.userFriends);
      setFriendRequests(data.friendRequests);
      setConnectionRequests(data.connectionRequests);
    } catch (err) {
      console.log("Error fetching friend info from server");
      console.log(err);
    }
  };

  const updateUI = (freshUser, fresh) => {
    const basicUser = {
      username: freshUser.username,
      email: freshUser.email,
      avatarUrl: freshUser.avatarUrl,
      id: freshUser.id,
      birthday: freshUser.birthday,
      createdAt: freshUser.createAt,
    };
    setUser(basicUser);
    localStorage.setItem("user", JSON.stringify(basicUser));
    buildEventsMap([...freshUser.events, ...holidays]);
    const sortedReminders = freshUser.reminders.sort(
      (a, b) => new Date(a.time) - new Date(b.time),
    );
    buildRemindersMap(sortedReminders);
    const sortedLists = freshUser.lists.sort(
      (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
    );
    const sortedTasks = freshUser.tasks.sort(
      (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
    );
    const sortedStickies = freshUser.stickies.sort(
      (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
    );
    setNotifSubs(freshUser.notifSub || []);
    setLists(sortedLists);
    setKanbans(freshUser.kanbans);
    setStickies(sortedStickies);
    M_GenerateQrCode(freshUser.email);
    setUserTasks(sortedTasks);
    if (fresh === true) {
      continueRequests(freshUser);
    }
  };

  useEffect(() => {
    if (authToken) {
      M_Async_LoadApp();
    }
    if (!authToken && user) {
      console.log("No token user there");
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      localDB.removeAuth();
      setUser(false);
    }
  }, [authToken]);

  const M_UpdateNotifSubLastSeen = async () => {
    const registration = await navigator.serviceWorker.ready;
    let currentSub = await registration.pushManager.getSubscription();

    const endpointToUpdate = currentSub?.endpoint || null;

    const newSubs = parseNotifSubs(notifSubs).map((s) => {
      if (endpointToUpdate === null) {
        return JSON.stringify(s);
      }
      if (s.endpoint === endpointToUpdate) {
        return JSON.stringify({ ...s, lastSeenAt: new Date() });
      }
      return JSON.stringify(s);
    });

    try {
      await updateLastSeenOnDevice(newSubs, authToken);
    } catch (err) {
      console.log(
        "Error calling API to update last seen on notification subscriptions",
      );
      console.log(err);
    }
  };

  const M_Async_LoadApp = async () => {
    try {
      const userData = await getUserData(authToken);

      const responseTypeForUserData = userData.headers["x-sw-source"];

      // The service worker will be first grabbing stale cache quickly. Make sure this is not the case before
      // calling updateUI(). User data MUST be server fresh because down the road notification subscriptions are made
      const isFresh = responseTypeForUserData === "network";

      if (!userData?.data) {
        throw new Error(
          "getUserData() succeeded but returned no data. Server error",
        );
      }

      const freshUser = userData.data?.user;
      updateUI(freshUser, isFresh);
      registerServiceWorkerSync();
      M_UpdateNotifSubLastSeen();
    } catch (err) {
      console.log("Error fetching user data from the server");
      console.log(err);
    }
  };

  const M_GenerateQrCode = (userEmail) => {
    const qr = QRCode(0, "L");
    const data = `https://calendar-next-gen-production.up.railway.app/friends/add/request/qrcode/${userEmail}`;
    qr.addData(data);
    qr.make();
    const qrCodeDataUrl = qr.createDataURL(4);
    setQrCodeUrl(qrCodeDataUrl);
  };

  const M_FetchGoogleEvents = (googleToken) => {
    setSystemNotif({ show: false });
    getGoogleCalendarEvents(authToken, googleToken)
      .then((res) => {
        const events = res.data.events;
        console.log("Here are your google events pulled in from google");
        console.log(events);
      })
      .catch((err) => console.log(err));
  };

  const M_RequestPermissionsAndSubscribe = async () => {
    try {
      return requestAndSubscribe(authToken)
        .then((res) => {
          if (res === null) {
            // Do something here. Maybe ask for permissions via notification or show notification
            // to remind user that they do not have permissions accepted
            return null;
          }
          return res.json();
        })
        .then((data) => {
          if (data === null) {
            return;
          }
          setUser(data.user);
          setNotifSubs(data.user.notifSub);
          localStorage.setItem("authToken", data.token);
          localStorage.setItem("user", JSON.stringify(data.user));
          send(data.token, data.user);
        })
        .catch((err) => {
          console.log("Error calling request and subscribe to notifications");
          console.log(err);
        });
    } catch (err) {
      console.error(
        "Error getting service worker registration after calling request and subscribe:",
        err,
      );
    }
  };

  const closeSSEConnection = () => {
    sseRequestGenerationRef.current += 1;
    sseConnectionRef.current?.close();
    sseConnectionRef.current = null;
    sseUserIdRef.current = null;
  };

  const send = async (token, currentUser) => {
    if (!currentUser?.id) return;
    const userId = currentUser.id;
    if (sseUserIdRef.current !== userId) closeSSEConnection();
    if (!sseConnectionRef.current) {
      const source = getNotifications(userId);
      sseConnectionRef.current = source;
      sseUserIdRef.current = userId;
      source.addEventListener("open", () => {
        console.info("SSE notification stream connected");
      });
      source.addEventListener("message", (event) => {
        try {
          const notification = JSON.parse(event.data);
          setNotifications((prev) =>
            prev.some((item) => item.id === notification.id)
              ? prev
              : [notification, ...prev],
          );
          setReminderNotifications((prev) =>
            prev.some((item) => item.id === notification.id)
              ? prev
              : [...prev, notification],
          );
        } catch (error) {
          console.error("Invalid SSE notification payload", error);
        }
      });
      source.addEventListener("error", () => {
        // Native EventSource automatically reconnects after transient errors.
        if (source.readyState === EventSource.CLOSED) {
          console.warn(
            "SSE stream closed; awaiting next login/session initialization",
          );
        } else {
          console.info("SSE stream interrupted; browser reconnecting");
        }
      });
    }
    const generation = ++sseRequestGenerationRef.current;
    try {
      const response = await getNotificationsAtStart(
        currentUser.username,
        token,
      );
      if (generation !== sseRequestGenerationRef.current) return;
      const oldNotifs = response.data.notifs || [];
      setNotifications((prev) => {
        const merged = new Map();
        for (const notification of [...oldNotifs, ...prev]) {
          merged.set(notification.id, notification);
        }
        return [...merged.values()].sort(
          (a, b) => new Date(b.time) - new Date(a.time),
        );
      });
    } catch (error) {
      console.error("Unable to load notification history", error);
    }
  };

  useEffect(() => {
    if (!authToken || !user?.id) closeSSEConnection();
  }, [authToken, user?.id]);

  useEffect(() => () => closeSSEConnection(), []);

  const markAsReadClient = (notifId) => {
    setNotifications((prevNotifs) => {
      const updated = prevNotifs.map((notif) =>
        notif.id === notifId ? { ...notif, read: true } : notif,
      );
      const sorted = updated.sort((a, b) => b.time - a.time);
      return sorted;
    });
    setSystemNotif({ show: false });
    markAsRead(notifId);
  };

  const createIndexedDb = () => {
    const request = indexedDB.open("myCalngDB", myLocalDBVersion);
    const calngIndexDBManager = new IndexedDBManager(request);
    setLocalDB(calngIndexDBManager);
  };

  const registerServiceWorkerSync = () => {
    navigator.serviceWorker.ready.then((registration) => {
      if ("periodicSync" in registration) {
        console.log("Period sync in service worker");
        return registration.periodicSync.getTags().then((tags) => {
          if (tags.includes("periodic-sync")) {
            return;
          }
          registration.periodicSync.register("periodic-sync", {
            minInterval: 24 * 60 * 60 * 1000,
          });
        });
      }
      // Commenting out background sync for now until I am ready to implement the logic
      // return registration.sync.register("background-sync");
    });
  };

  return (
    <UserContext.Provider
      value={{
        user,
        googleToken,
        isOnline,
        lists,
        notifications,
        systemNotif,
        friends,
        stickies,
        userTasks,
        kanbans,
        qrCodeUrl,
        connectionRequests,
        friendRequests,
        upcoming,
        preferences,
        eventMap,
        eventsById,
        getEventById,
        eventMapDays,
        weatherData,
        location,
        usersLocations,
        notifSubs,
        localDB,
        reminderNotifications,
        timers,
        remindersMap,
        remindersById,
        getReminderById,
        setRemindersMap,
        setTimers,
        setReminderNotifications,
        setNotifSubs,
        setWeatherData,
        setLocation,
        setUsersLocations,
        setPreferences,
        setKanbans,
        setConnectionRequests,
        setFriendRequests,
        setUserTasks,
        setStickies,
        setFriends,
        setSystemNotif,
        setNotifications,
        setLists,
        setUser,
        setGoogleToken,
        setAuthToken,
        setEventMap,
        setEventMapDays,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
